import { colors } from '@lmc/common/constants'
import type { CarePlan, CarePlanAdl } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { riskAssessmentFormConstants } from 'locale/riskAssessmentForm'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { EditTooltip } from 'screens/Residents/screens/ResidentProfileScreen/components/EditTooltip'
import styled from 'styled-components'
import type { PopulatedCarePlan } from 'types/shared'
import { getNominative } from '../../../common/utils'
import Operations from '../../../operations'
import { loadList } from '../../../operations/Data'
import MultiSelectField from '../../fields/types/MultiSelectField'
import { ImageSection } from '../ListItem/components'
import { Spinner } from '../Spinner'

export interface CarePlanAdlExtended extends Omit<CarePlanAdl, 'icon'> {
  noFurtherSupportRequired: boolean
  value: any
  carePlan?: CarePlan
  icon: string
  iconText: string
}

interface RootState {
  data: {
    'care-plan-adls': CarePlanAdlExtended[]
  }
  carePlans?: {
    adls: CarePlanAdlExtended[]
    adlsResidentId?: string
  }
  global: {
    home: {
      preferredCarePlansNominative: string
    }
  }
}

export type CarePlanSelectorVariant = 'default' | 'consent'

export interface CarePlanSelectorProps {
  selectedCarePlans: CarePlanAdlExtended[] | PopulatedCarePlan[] | string[]
  onChange: (carePlans: CarePlanAdlExtended[]) => void
  warningMessage?: string
  readonly?: boolean
  label?: React.ReactNode
  residentId?: string
  variant?: CarePlanSelectorVariant
}

export const CarePlanSelector: React.FC<CarePlanSelectorProps> = ({
  selectedCarePlans,
  onChange,
  warningMessage,
  readonly = false,
  label,
  residentId,
  variant = 'default'
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS_FORM)
  const dispatch = useDispatch()
  const location = useLocation()

  // Get data from Redux store
  const carePlans = useSelector((state: RootState) => state.carePlans)
  const carePlanAdls = useSelector(
    (state: RootState) => state.data['care-plan-adls']
  )
  const carePlanNominative = useSelector(
    (state: RootState) => state.global.home.preferredCarePlansNominative
  )

  // Fetch care-plan-adls if not already loaded
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!carePlanAdls) {
      dispatch(loadList('care-plan-adls') as any)
    }

    if (residentId) {
      dispatch(Operations.fetchResidentAdls(residentId) as any)
    }
  }, [dispatch, carePlanAdls, residentId])

  // Derive care plan options from Redux state.
  // `state.carePlans.adls` is a single global slot that holds whichever
  // resident's ADLs were fetched last. Only trust it when it was loaded for
  // the resident we're rendering for; otherwise we'd show (and let staff link)
  // another resident's care plans during the window before the re-fetch lands.
  const slotIsForResident =
    !residentId || carePlans?.adlsResidentId === residentId
  const residentScopedAdls = slotIsForResident ? carePlans?.adls : undefined
  const carePlansAdlsSelection = residentScopedAdls || carePlanAdls || []

  const carePlanOptions = carePlansAdlsSelection
    ?.filter(
      (item) =>
        item?.carePlan &&
        !item?.carePlan?.noFurtherSupportRequired &&
        item?.carePlan?.nextReviewDate
    )
    .map((item) => ({
      id: item.carePlan?.id,
      name: item.name,
      icon: item?.icon,
      color: item?.color
    }))

  // Normalize selectedCarePlans to CarePlanAdlExtended[]
  // Handles three input formats: string IDs, PopulatedCarePlan objects, or CarePlanAdlExtended objects
  // Returns null when data is still loading, empty array when loaded with no selections
  const normalizedSelectedCarePlans: CarePlanAdlExtended[] | null =
    React.useMemo(() => {
      if (!selectedCarePlans || selectedCarePlans.length === 0) {
        return []
      }

      const first = selectedCarePlans[0]

      // String IDs — resolve via unfiltered ADL data
      if (typeof first === 'string') {
        if (carePlansAdlsSelection.length === 0) {
          return null
        }
        const ids = selectedCarePlans as string[]
        return ids
          .map((id) => {
            const adl = carePlansAdlsSelection.find(
              (a) => a.carePlan?.id === id
            )
            if (!adl) return undefined
            return {
              id: adl.carePlan?.id,
              name: adl.name,
              icon: adl?.icon,
              color: adl?.color
            } as CarePlanAdlExtended
          })
          .filter((adl): adl is CarePlanAdlExtended => adl !== undefined)
      }

      // Populated objects from API — enrich with icon/color from ADL data
      if ('carePlanAdl' in first) {
        const populated = selectedCarePlans as PopulatedCarePlan[]
        return populated.map((cp) => {
          const adl = carePlansAdlsSelection.find(
            (a) => a.carePlan?.id === cp.id
          )
          return {
            id: cp.id,
            name: cp.customName || cp.carePlanAdl?.name || '',
            noFurtherSupportRequired: cp.noFurtherSupportRequired,
            icon: adl?.icon || '',
            color: adl?.color || '',
            iconText: adl?.iconText || '',
            value: cp.id
          } as CarePlanAdlExtended
        })
      }

      // Already CarePlanAdlExtended objects
      return selectedCarePlans as CarePlanAdlExtended[]
    }, [selectedCarePlans, carePlansAdlsSelection])

  // Format option label for MultiSelectField
  const carePlanSelectOption = useCallback(
    ({ name, icon, color }: { name: string; icon: string; color: string }) => (
      <CarePlanOptionWrapper>
        <ImageSection popup iconColor={color} icon={icon} />
        <CarePlanName>{name}</CarePlanName>
      </CarePlanOptionWrapper>
    ),
    []
  )

  // Handle selection change
  const handleCarePlanIds = useCallback(
    (value: CarePlanAdlExtended[]) => {
      onChange(value)
    },
    [onChange]
  )

  const attachedToTranslation = t(riskAssessmentFormConstants.ATTACHED_TO, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLANS
    )
  })

  const isCarePlanListLoading = normalizedSelectedCarePlans === null

  if (readonly) {
    return (
      <>
        <CarePlanSelectorWrapper $variant={variant}>
          <ReadonlyWrapper>
            {label ? label : <Label as="p2">{attachedToTranslation}</Label>}
            {isCarePlanListLoading ? (
              <Spinner style={{ paddingTop: 0, width: 'fit-content' }} />
            ) : normalizedSelectedCarePlans.length > 0 ? (
              <ReadonlyIconsContainer>
                {normalizedSelectedCarePlans.map((carePlan) => {
                  return carePlan ? (
                    <EditTooltip
                      key={carePlan?.id}
                      title={
                        <TooltipLink
                          to={`/residents/${residentId}/care-plans/${carePlan?.id}`}
                          state={{ referrer: location.pathname }}
                        >
                          <TooltipContent>
                            <ImageSection
                              popup
                              iconColor={carePlan.color}
                              icon={carePlan.icon}
                            />
                            {carePlan.name}
                          </TooltipContent>
                        </TooltipLink>
                      }
                    >
                      <TooltipWrapper>
                        <ImageSection
                          popup
                          iconColor={carePlan.color}
                          icon={carePlan.icon}
                        />
                      </TooltipWrapper>
                    </EditTooltip>
                  ) : null
                })}
              </ReadonlyIconsContainer>
            ) : (
              <Text level={1.5}>
                {t(riskAssessmentFormConstants.NOT_LINKED_TO_ANY_CARE_PLAN)}
              </Text>
            )}
          </ReadonlyWrapper>
        </CarePlanSelectorWrapper>
        {warningMessage && <WarningText as="p3">{warningMessage}</WarningText>}
      </>
    )
  }

  if (normalizedSelectedCarePlans === null) {
    return (
      <CarePlanSelectorWrapper $variant={variant}>
        <Spinner style={{ paddingTop: 0, width: 'fit-content' }} />
      </CarePlanSelectorWrapper>
    )
  }

  return (
    <>
      <CarePlanSelectorWrapper $variant={variant}>
        <MultiSelectField
          formKey={undefined}
          label={label ? label : attachedToTranslation}
          customOnChange={handleCarePlanIds}
          onChange={() => {}}
          options={carePlanOptions || []}
          formatOptionLabel={carePlanSelectOption}
          customValues={normalizedSelectedCarePlans}
          centerLabel
          errors={undefined}
          style={null}
          placeholder={''}
          values={[]}
        />
      </CarePlanSelectorWrapper>
      {warningMessage && <WarningText as="p3">{warningMessage}</WarningText>}
    </>
  )
}

const CarePlanSelectorWrapper = styled.div<{
  $variant: CarePlanSelectorVariant
}>`
  margin-top: 10px;
  background: ${colors.white};
  border-radius: 15px;
  padding: ${({ $variant }) => ($variant === 'consent' ? '8px 16px' : '15px')};
  display: flex;
`

const WarningText = styled(Text)`
  color: ${colors.red};
`

const CarePlanOptionWrapper = styled.div`
  align-items: center;
  display: flex;
`
CarePlanOptionWrapper.displayName = 'CarePlanOptionWrapper'

const CarePlanName = styled.span``

const ReadonlyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const Label = styled(Text)`
  font-weight: 700;
  font-size: 16px;
`

const ReadonlyIconsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TooltipWrapper = styled.div`
  display: inline-flex;
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`

const TooltipLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
`
